import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Translate } from 'core/translate';
import { SvgIcon } from 'core/icons';
import { combineClassNames } from 'core/css';
import OptionCard, {
  OptionCardPrimarySection,
} from '../../components/cards/optionCard/OptionCard';
import { BYO_CLOSE, dispatchCustomEvent } from '../../utils/customMessage';
import PackagePricing from '../../components/packages/PackagePricing';
import { useTranslate } from '../../../core/translate';

export const SimpleOptionInfo = ({
  className,
  option,
  analyticsKey,
  analyticsKey2,
  onDetails,
}) => {
  const { longCfd, disclaimer } = option;
  const translate = useTranslate();

  const onClick = (event) => {
    event.stopPropagation();
    dispatchCustomEvent(BYO_CLOSE);
    onDetails(option);
  };

  return longCfd || disclaimer ? (
    <button
      className={combineClassNames(
        'simple-option-info-details',
        'stat-image-link',
        className
      )}
      data-dtm={analyticsKey}
      data-dtm2={analyticsKey2}
      onClick={onClick}
      aria-label={translate('info.icon.aria.label.v3')}>
      <SvgIcon name="info" className="simple-option-info__icon" />
    </button>
  ) : null;
};

export const SimpleOptionPrice = ({ option }) => {
  const { requiresPackage, includedInPackage, msrpDisplay } = option;
  const displayedMsrp = get(msrpDisplay, 'amount.text', '');
  const displayedText = get(msrpDisplay, 'display', '');

  if (requiresPackage) {
    return <Translate value="component.pricing.package.requires" />;
  }
  if (includedInPackage) {
    return <Translate value="component.pricing.package.includes" />;
  }

  return displayedText || displayedMsrp;
};

export const SimpleOptionInfoDetails = ({
  option,
  analyticsKey,
  analyticsKey2,
  onDetails,
  className,
  type,
  hideInfoIcon,
}) => {
  const { description, code } = option;

  return (
    <div className={combineClassNames(['simple-option__details', className])}>
      <div
        className="simple-option__description hide-print"
        dangerouslySetInnerHTML={{ __html: description }}
      />
      <div
        className="simple-option__description show-print"
        dangerouslySetInnerHTML={{
          __html: `${description}${code ? ` (${code})` : ''}`,
        }}
      />
      <div className="simple-option__price-container">
        {!hideInfoIcon && (
          <SimpleOptionInfo
            option={option}
            onDetails={onDetails}
            analyticsKey={analyticsKey}
            analyticsKey2={analyticsKey2}
          />
        )}
        <div className="simple-option__price">
          {type === 'package' ? (
            <PackagePricing
              option={option}
              className="package-option__pricing"
            />
          ) : (
            <SimpleOptionPrice option={option} />
          )}
        </div>
      </div>
    </div>
  );
};

const SimpleOptionCard = ({
  className,
  option,
  role,
  type,
  onChange,
  onDetails,
  analyticsKey,
  analyticsKey2,
  disabled,
  optionMarker,
  isUpdating,
  selectingOption,
  hideInfoIcon,
  checked,
  displayOnly = false,
}) => {
  const { smallImageUrl, code } = option;
  const onClick = () => {
    onChange(option);
  };
  const onKeyDown = (event) => {
    if (disabled) {
      return;
    }

    const spaceBar = '32';
    if (event.keyCode == spaceBar) {
      onChange(option);
      event.preventDefault();
    }
  };

  const isUpdatingOption = isUpdating && get(selectingOption, 'code') === code;

  return (
    <OptionCard
      option={option}
      className={className}
      type={type}
      key={code}
      role={role}
      image={smallImageUrl}
      selected={checked}
      aria-checked={checked}
      onChange={onClick}
      onKeyDown={onKeyDown}
      disabled={disabled}
      optionMarker={optionMarker}
      analyticsKey={analyticsKey}
      analyticsKey2={analyticsKey2}
      isUpdating={isUpdating}
      isUpdatingOption={isUpdatingOption}
      displayOnly={displayOnly}>
      <OptionCardPrimarySection>
        <SimpleOptionInfoDetails
          option={option}
          analyticsKey={analyticsKey}
          analyticsKey2={option.description}
          onDetails={onDetails}
          type={type}
          hideInfoIcon={hideInfoIcon}
        />
      </OptionCardPrimarySection>
    </OptionCard>
  );
};

SimpleOptionCard.defaultProps = {
  role: 'radio',
  disabled: false,
  type: '',
  showUpdatingInAll: true,
  hideInfoIcon: false,
  showUserSelection: false,
  checked: false,
};

SimpleOptionCard.propTypes = {
  type: PropTypes.string,
  role: PropTypes.string,
  disabled: PropTypes.bool,
  showUpdatingInAll: PropTypes.bool,
  hideInfoIcon: PropTypes.bool,
  showUserSelection: PropTypes.bool,
  checked: PropTypes.bool,
};

export default SimpleOptionCard;
